import { useState } from 'react'
import { X, AlertTriangle, Cloud, Lightbulb, HelpCircle, MessageCircle, MapPin } from 'lucide-react'
import { CommentCategory, CommentType, CreateCommentData } from '../../types/comments'

interface AddCommentModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: CreateCommentData) => Promise<void>
  routeId: string
  type: CommentType
  coordinates?: [number, number]
  distanceAlongRoute?: number
}

const CATEGORY_OPTIONS: {
  value: CommentCategory
  label: string
  icon: React.ComponentType<any>
  description: string
}[] = [
  {
    value: 'hazard',
    label: 'Hazard',
    icon: AlertTriangle,
    description: 'Report dangers or obstacles'
  },
  {
    value: 'condition',
    label: 'Condition',
    icon: Cloud,
    description: 'Current route conditions'
  },
  {
    value: 'tip',
    label: 'Tip',
    icon: Lightbulb,
    description: 'Helpful advice or suggestions'
  },
  {
    value: 'question',
    label: 'Question',
    icon: HelpCircle,
    description: 'Ask about the route'
  },
  {
    value: 'discussion',
    label: 'Discussion',
    icon: MessageCircle,
    description: 'General comments'
  }
]

export function AddCommentModal({
  isOpen,
  onClose,
  onSubmit,
  routeId,
  type,
  coordinates,
  distanceAlongRoute
}: AddCommentModalProps) {
  const [category, setCategory] = useState<CommentCategory>('tip')
  const [text, setText] = useState('')
  const [validDays, setValidDays] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!text.trim()) {
      setError('Please enter a comment')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const commentData: CreateCommentData = {
        routeId,
        type,
        category,
        text: text.trim(),
        coordinates: coordinates ? {
          lng: coordinates[0],
          lat: coordinates[1]
        } : undefined,
        distanceAlongRoute
      }

      // Add validity period if specified
      if (validDays && (category === 'hazard' || category === 'condition')) {
        const validUntil = new Date()
        validUntil.setDate(validUntil.getDate() + validDays)
        commentData.validUntil = validUntil
      }

      await onSubmit(commentData)
      
      // Reset form and close
      setText('')
      setCategory('tip')
      setValidDays(null)
      onClose()
    } catch (err) {
      console.error('Error submitting comment:', err)
      setError('Failed to add comment. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" style={{ fontSize: 'min(1rem, 1.5vw)', paddingBottom: '20vh' }}>
      <div className="bg-gray-800 rounded-xl shadow-2xl flex flex-col border border-gray-700/50 overflow-hidden" style={{ width: '90vw', maxWidth: '500px', maxHeight: '75vh' }}>
        {/* Header */}
        <div className="border-b border-gray-700 flex items-center justify-between bg-gray-800" style={{ padding: '0.75em' }}>
          <h2 className="font-semibold text-white flex items-center" style={{ fontSize: '1em', gap: '0.5em' }}>
            {type === 'pinned' && <MapPin className="text-emerald-500" style={{ width: '1em', height: '1em' }} />}
            Add {type === 'pinned' ? 'Location' : 'General'} Comment
          </h2>
          <button
            onClick={onClose}
            className="hover:bg-gray-700 rounded-lg transition-colors"
            style={{ padding: '0.25em' }}
          >
            <X className="text-gray-400" style={{ width: '1.25em', height: '1.25em' }} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} id="comment-form" className="flex-1 overflow-y-auto" style={{ padding: '1em' }}>
          {/* Distance indicator for pinned comments */}
          {type === 'pinned' && distanceAlongRoute !== undefined && (
            <div className="text-gray-400" style={{ fontSize: '0.875em', marginBottom: '1em' }}>
              <MapPin className="inline" style={{ width: '1em', height: '1em', marginRight: '0.25em' }} />
              {distanceAlongRoute.toFixed(1)} km along route
            </div>
          )}

          {/* Category Selection */}
          <div style={{ marginBottom: '1.5em' }}>
            <label className="block font-medium text-gray-200" style={{ fontSize: '0.875em', marginBottom: '0.5em' }}>
              Category
            </label>
            <div className="grid grid-cols-2" style={{ gap: '0.5em' }}>
              {CATEGORY_OPTIONS.map(option => {
                const Icon = option.icon
                const isSelected = category === option.value
                
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setCategory(option.value)}
                    className={`
                      rounded-lg border-2 transition-all
                      ${isSelected 
                        ? 'border-emerald-500 bg-emerald-900/20' 
                        : 'border-gray-600 hover:border-gray-500'
                      }
                    `}
                    style={{ padding: '0.75em' }}
                  >
                    <div className="flex flex-col items-center" style={{ gap: '0.25em' }}>
                      <Icon className={`${isSelected ? 'text-emerald-400' : 'text-gray-400'}`} style={{ width: '1.25em', height: '1.25em' }} />
                      <span className={`font-medium ${isSelected ? 'text-emerald-400' : 'text-gray-300'}`} style={{ fontSize: '0.75em' }}>
                        {option.label}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
            <p className="text-gray-400" style={{ fontSize: '0.75em', marginTop: '0.25em' }}>
              {CATEGORY_OPTIONS.find(opt => opt.value === category)?.description}
            </p>
          </div>

          {/* Comment Text */}
          <div style={{ marginBottom: '1.5em' }}>
            <label htmlFor="comment-text" className="block font-medium text-gray-200" style={{ fontSize: '0.875em', marginBottom: '0.5em' }}>
              Comment
            </label>
            <textarea
              id="comment-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              style={{ padding: '0.75em', fontSize: '0.875em' }}
              rows={4}
              placeholder={
                category === 'hazard' ? 'Describe the hazard and its location...' :
                category === 'condition' ? 'Describe current conditions...' :
                category === 'tip' ? 'Share your helpful tip...' :
                category === 'question' ? 'What would you like to know?' :
                'Add your comment...'
              }
              required
            />
            <p className="text-gray-400" style={{ fontSize: '0.75em', marginTop: '0.25em' }}>
              {text.length}/500 characters
            </p>
          </div>

          {/* Validity Period (for hazards and conditions) */}
          {(category === 'hazard' || category === 'condition') && (
            <div style={{ marginBottom: '1.5em' }}>
              <label className="block font-medium text-gray-200" style={{ fontSize: '0.875em', marginBottom: '0.5em' }}>
                Valid for (optional)
              </label>
              <div className="flex" style={{ gap: '0.5em' }}>
                {[1, 3, 7, 14, 30].map(days => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setValidDays(validDays === days ? null : days)}
                    className={`
                      rounded-md transition-colors
                      ${validDays === days 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }
                    `}
                    style={{ padding: '0.25em 0.75em', fontSize: '0.875em' }}
                  >
                    {days}d
                  </button>
                ))}
              </div>
              <p className="text-gray-400" style={{ fontSize: '0.75em', marginTop: '0.25em' }}>
                How long this {category} is expected to last
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/20 border border-red-700 rounded-lg" style={{ padding: '0.75em', marginBottom: '1em' }}>
              <p className="text-red-400" style={{ fontSize: '0.875em' }}>{error}</p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="border-t border-gray-700 flex" style={{ padding: '1em', gap: '0.75em' }}>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            style={{ padding: '0.75em 1em', fontSize: '0.875em' }}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="comment-form"
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ padding: '0.75em 1em', fontSize: '0.875em' }}
            disabled={isSubmitting || !text.trim()}
          >
            {isSubmitting ? 'Adding...' : 'Add Comment'}
          </button>
        </div>
      </div>
    </div>
  )
}